import React, { useEffect, useMemo, useRef, useState } from 'react'
import { withLDConsumer } from 'launchdarkly-react-client-sdk'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { isEmpty, isNil } from 'lodash'
import queryString from 'query-string'

import { Loader } from '@/components/loaders/Loader'
import { Button } from '@/components/buttons/Button'
import { fetchPlaylist } from '@/reducers/playlists/playlists.actions'
import {
  CALL_DETAIL_PAGE,
  fetchCallExplorer,
  trackPageView,
} from '@/reducers/callSearch/callSearch.actions'
import {
  createChangeRequest,
  fetchTranscriptChangeRequests,
} from '@/reducers/voiceAgents/voiceAgents.actions'
import { setFocusedTranscriptChangeRequest } from '@/reducers/voiceAgents/voiceAgents.redux'
import { parseParams } from '@/reducers/callSearch/helpers'
import { VideoPlayerProvider } from '@/components/media/VideoPlayerContext'
import { AudioPlayerProvider } from '@/components/media/AudioPlayerContext'
import { TranscriptProvider } from '@/components/transcript/TranscriptContext'
import { MediaPlayer } from '@/components/media/MediaPlayer'
import { isOpenRequest } from '@/views/VoiceAgents/changeRequestHelpers'
import { CHANNEL_TYPE } from '@/utils/constants'

import {
  CALL_EXPLORER_TAB_ENUMS,
  getContextFromEvent,
  updateCallExplorerParams,
} from './components/helpers'
import { CallExplorerTabs } from './CallExplorerTabs'
import { CallExplorerHeader } from './components/CallExplorerHeader'
import { PlaylistDrawer } from '../Playlists/components/PlaylistDrawer'

import './CallExplorerPage.scss'

const CallExplorerPageComponent = ({ flags }) => {
  const [fetched, setFetched] = useState(false)
  const { id: callId } = useParams()
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const { search } = location
  const { callExplorer, callExplorerLoading, error } = useSelector((state) => state.callSearch)
  const transcriptComments = useSelector(
    (state) => state.voiceAgents.transcriptChangeRequests.items
  )
  const focusedCommentId = useSelector(
    (state) => state.voiceAgents.transcriptChangeRequests.focusedId
  )
  const isCommenting = useSelector((state) =>
    Boolean(state.voiceAgents.loading.createChangeRequest)
  )
  const { playlist } = useSelector((state) => state.playlists)
  const { playlist_calls: playlistItems = [] } = playlist || {}
  const playlistCalls = playlistItems.filter((c) => c.record_type === CHANNEL_TYPE.CALL)
  const { playlistUuid, channel } = queryString.parse(search)
  const showTranscriptSelector = flags?.callsExplorerTranscriptHypothesis

  // Deep-link from the by-user comments list (`?comment=<id>`): focus that
  // comment once its list has loaded. Parsed once on mount and consumed after
  // the fetch so the focus survives the per-call reset; the param is then stripped.
  const pendingCommentFocusRef = useRef(
    (() => {
      const { comment } = queryString.parse(search)
      const id = Number(Array.isArray(comment) ? comment[0] : comment)
      return Number.isFinite(id) ? id : null
    })()
  )

  const {
    transcript,
    postcall_transcript: postcallTranscript,
    pretty_transcript: prettyTranscript,
    metadata = {},
    analysis,
  } = callExplorer

  // is_ai_call lives on the call-detail metadata (the call-search rows expose it
  // top-level, but the detail response nests it under metadata).
  const { organization_id: organizationId, record_type, is_ai_call: isAiCall = false } = metadata
  // Determine call type from URL param or metadata
  const callType = channel || record_type || CHANNEL_TYPE.CALL
  // Transcript commenting is only meaningful on voice-agent calls; the BE rejects
  // it on everything else, so gate on the call-detail's is_ai_call flag. The
  // detail (callId + is_ai_call) lands atomically via setCallExplorerCall, so a
  // callId match means the is_ai_call we're reading belongs to *this* call rather
  // than the previous one still in state during a call→call navigation.
  const isCallDetailLoaded = String(callExplorer.callId) === String(callId)
  const transcriptCommentingEnabled = callType === CHANNEL_TYPE.CALL && Boolean(isAiCall)

  // The focused comment can be resolved (e.g. deep-linked from the by-user list),
  // so resolve its turn from the *full* list, not the open-only `items` below —
  // otherwise scrolling to a resolved comment's turn would never fire.
  const focusedComment = transcriptComments.find((request) => request.id === focusedCommentId)

  // Config the transcript reads (via TranscriptContext) to let users comment on
  // assistant turns and to paint existing comment spans back onto the text.
  const transcriptCommentsConfig = {
    callId: Number(callId),
    // Only open comments paint highlight marks on the transcript (mirrors the
    // prompt side); resolved ones stay in the tab's Resolved filter. This
    // config feeds the transcript marks only — the tab reads the slice directly.
    items: transcriptComments.filter(isOpenRequest),
    enabled: transcriptCommentingEnabled,
    busy: isCommenting,
    focusedId: focusedCommentId,
    // Turn to scroll to when a comment is focused — open or resolved.
    focusedTurnStartMs: focusedComment?.turn?.startMs ?? null,
    onCreate: (payload) =>
      dispatch(
        createChangeRequest({ requestType: 'transcript', callId: Number(callId), ...payload })
      ),
    onMarkClick: (requestId) => {
      dispatch(setFocusedTranscriptChangeRequest(requestId))
      updateCallExplorerParams(location, history, {
        tab: CALL_EXPLORER_TAB_ENUMS.AGENT_COMMENTS,
      })
    },
  }

  const getHighlightedEventsFromParams = () => {
    const { deck, checklist, notifications, postcall, keywords } = parseParams(search)
    const highlightedContext = [
      ...deck.selected,
      ...checklist.selected,
      ...notifications.selected,
      ...postcall.selected,
    ].map((context) => {
      const contextArr = context.split(' - ')
      contextArr.shift()

      if (['checklist', 'notifications'].includes(contextArr[0])) {
        return [contextArr[0], contextArr[2]].join(' - ')
      }

      return contextArr.join(' - ')
    })
    if (!isEmpty(keywords)) {
      keywords.map((keyword) => highlightedContext.push(`keywords - ${keyword.value}`))
    }

    const newHighlightedEvents = analysis.filter((eventObj) => {
      const eventContext = getContextFromEvent(eventObj)

      return highlightedContext.some((contextStr) => eventContext === contextStr)
    })
    return newHighlightedEvents.sort((a, b) => a.timestamp - b.timestamp)
  }

  const fetchCall = async () => {
    const { keywords } = parseParams(search)
    await dispatch(fetchCallExplorer({ id: callId, keywords }))
    setFetched(true)
  }

  useEffect(() => {
    fetchCall()
  }, [callId])

  // Drop any focus from the previous call before its list is replaced, so the
  // new call's transcript doesn't briefly scroll to a stale focused turn.
  useEffect(() => {
    dispatch(setFocusedTranscriptChangeRequest(null))
  }, [callId])

  // Load transcript comments at the page level (not in the Transcript Comments tab)
  // so the inline transcript marks stay populated even while that tab is closed.
  // Only AI calls have transcript comments — fetching for a non-AI call 422s the
  // BE into an error toast — so wait until the detail confirms this is an AI call.
  useEffect(() => {
    if (callId && isAiCall && isCallDetailLoaded) {
      dispatch(fetchTranscriptChangeRequests(Number(callId)))
    }
  }, [callId, isAiCall, isCallDetailLoaded])

  // Consume a deep-link focus (`?comment=<id>`) only once the transcript is on
  // screen (`fetched`) and its comments are loaded. Setting focus before the
  // transcript mounts lets the playback auto-scroll (which parks at the top on
  // mount) win the scroll race; deferring until after mount makes the focus
  // change scroll the already-mounted transcript to the comment's turn. The param
  // is dropped after so a refresh or back-nav doesn't re-focus.
  useEffect(() => {
    if (!fetched || isNil(pendingCommentFocusRef.current) || isEmpty(transcriptComments)) {
      return
    }
    dispatch(setFocusedTranscriptChangeRequest(pendingCommentFocusRef.current))
    pendingCommentFocusRef.current = null
    const params = queryString.parse(search)
    delete params.comment
    history.replace({ pathname: location.pathname, search: queryString.stringify(params) })
  }, [fetched, transcriptComments])

  useEffect(() => {
    if (organizationId && playlistUuid) {
      dispatch(fetchPlaylist(playlistUuid, organizationId))
    }
  }, [organizationId, playlistUuid])

  // Track page view for reporting - only fires when callId changes,
  // not when query params change (e.g., clicking events to anchor)
  useEffect(() => {
    if (callId) {
      dispatch(trackPageView(CALL_DETAIL_PAGE, callId))
    }
  }, [callId])

  const highlightedEvents = useMemo(getHighlightedEventsFromParams, [analysis])

  if (error.callExplorer) {
    return (
      <div>
        <p>Call in progress, check back later.</p>
        <Button
          type="button"
          primary
          onClick={() => {
            window.location.reload()
          }}
        >
          Reload
        </Button>
      </div>
    )
  }

  return (
    <>
      <CallExplorerHeader playlist={playlist} callId={callId} metadata={metadata} />

      <div className="call-explorer-page">
        {callExplorerLoading || !fetched ? (
          <div className="empty loading-container">
            <Loader centered data-testid="call-explorer-loading" />
          </div>
        ) : (
          <>
            <div className="flex-column flex-item call-explorer-panel">
              <AudioPlayerProvider>
                <VideoPlayerProvider>
                  <TranscriptProvider
                    transcript={transcript}
                    postcallTranscript={postcallTranscript}
                    prettyTranscript={prettyTranscript}
                    showTranscriptSelector={showTranscriptSelector}
                    comments={transcriptCommentsConfig}
                  >
                    <MediaPlayer
                      isTranscriptAvailable
                      transcriptMetadata={metadata}
                      transcriptEnableAutoScroll
                      showTranslateFeature={callType === CHANNEL_TYPE.CALL}
                    />
                  </TranscriptProvider>
                </VideoPlayerProvider>
              </AudioPlayerProvider>
            </div>

            <CallExplorerTabs
              highlightedEvents={highlightedEvents}
              callId={callId}
              organizationId={organizationId}
              callType={callType}
              isAiCall={Boolean(isAiCall)}
            />
          </>
        )}
        {playlistUuid && playlistCalls.length > 0 && (
          <PlaylistDrawer items={playlistCalls} currentItemId={callId} />
        )}
      </div>
    </>
  )
}

export default withLDConsumer()(CallExplorerPageComponent)
